import { httpClient, HttpError } from '@shared/api/http-client';
import { notificationService } from '@shared/services/notification-service';
import { t } from '@shared/i18n';
import type { SubmitConfig } from '@features/service-form/service-form-types';

export class ServiceFormSubmitter {
  async submit(config: SubmitConfig): Promise<void> {
    const {
      getData,
      preSubmit,
      transform,
      onClearCache,
      messages = {},
      successUrl = '/pages/submit-success.html',
      submitButtonEl,
      failureAction,
    } = config;

    if (preSubmit) {
      const confirmed = await preSubmit();
      if (!confirmed) return;
    }

    if (submitButtonEl) submitButtonEl.disabled = true;

    const loadingId = notificationService.loading(messages.loading ?? t('service.submitting'));

    try {
      let formData = getData();
      if (transform) formData = transform(formData);

      const result = await httpClient.post('/api/form-submit', formData);

      notificationService.remove(loadingId);

      if (!result.success) {
        throw new HttpError(result.message ?? '提交失败', result.status ?? 500);
      }

      onClearCache?.();
      notificationService.success(messages.success ?? t('service.submitSuccess'));

      setTimeout(() => {
        window.location.href = successUrl;
      }, 2000);
    } catch (error) {
      notificationService.remove(loadingId);

      const status = error instanceof HttpError ? error.status : 0;

      if (status === 401 || status === 403) {
        const actions = [
          {
            label: t('common.relogin'),
            action: () => {
              const returnPath = encodeURIComponent(
                window.location.pathname + window.location.search
              );
              window.location.href = `/pages/auth/login.html?return=${returnPath}`;
            },
            primary: true,
          },
        ];
        if (failureAction) actions.push({ ...failureAction, primary: false });

        notificationService.error(t('common.authExpired'), {
          actions,
          duration: 10000,
        });
      } else {
        notificationService.error(t('common.requestFailedSupportShort'), {
          actions: failureAction ? [{ ...failureAction, primary: true }] : undefined,
          duration: failureAction ? 10000 : undefined,
        });
      }
    } finally {
      if (submitButtonEl) submitButtonEl.disabled = false;
    }
  }
}

export const serviceFormSubmitter = new ServiceFormSubmitter();
